<template>
    <div class="mx-2 mt-4">
        <div class="card relative bg-base-100 shadow-lg border border-base-200 overflow-hidden h-[calc(100vh-6rem)] flex flex-col">
            <div class="absolute inset-x-0 top-0 h-1 bg-base-200/80">
                <div
                    class="h-full bg-primary/20 transition-all duration-300"
                    :style="{ width: `${detailData.static.complete_rate || 0}%` }"
                ></div>
            </div>
            <div class="card-body p-0 flex flex-col flex-1 min-h-0">
                <div class="border-b border-base-200/80 px-6 py-6">
                    <div class="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                        <div class="min-w-0">
                            <div class="flex items-center gap-3 flex-wrap">
                                <h2 class="text-xl font-semibold text-base-content">{{ $tt(operationCategoryTitle) }}</h2>
                                <div class="badge border-0 px-3 py-3 font-medium" :class="headerStatusClass">{{ headerStatusText }}</div>
                                <div class="badge badge-lg border-base-300/80 bg-base-100 font-medium">{{ $tt('总数 {count}', { count: detailData.static.total_count || 0 }) }}</div>
                                <div class="badge badge-lg border-base-300/80 bg-base-100 font-medium">{{ $tt('已完成 {count}', { count: detailData.static.complete_count || 0 }) }}</div>
                                <div class="badge badge-lg border-base-300/80 bg-base-100 font-medium">{{ $tt('进度 {rate}%', { rate: detailData.static.complete_rate || 0 }) }}</div>
                            </div>
                            <div class="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-base-content/55">
                                <span>{{ $tt('创建时间') }}: {{ detailData.created_at || '-' }}</span>
                                <span>{{ $tt('运行时间') }}: {{ detailData.run_time || '-' }}</span>
                                <span>{{ $tt('结束时间') }}: {{ detailData.complete_at || '-' }}</span>
                            </div>
                        </div>

                        <div v-if="detailData.status === 'running' || detailData.status === 'stop'" class="flex flex-wrap items-center gap-2">
                            <button
                                v-if="detailData.status === 'running'"
                                class="btn btn-sm btn-outline btn-error"
                                @click="$emit('stop-task')"
                            >{{ $tt('停止任务') }}</button>
                            <button
                                v-if="detailData.status === 'stop' && canResumeTask"
                                class="btn btn-sm btn-outline btn-success"
                                @click="$emit('resume-task')"
                            >{{ $tt('恢复任务') }}</button>
                        </div>
                    </div>
                </div>

                <div class="grid gap-0 lg:grid-cols-[280px_minmax(0,1fr)] xl:grid-cols-[300px_minmax(0,1fr)] scrape-overview-shell flex-1 min-h-0">
                    <aside class="border-b border-base-200/80 lg:border-b-0 lg:border-r lg:border-base-200/80 scrape-overview-sidebar overflow-y-auto">
                        <div class="p-3.5">
                            <div class="space-y-1.5">
                                <button
                                    v-for="group in visibleGroups"
                                    :key="group.key"
                                    class="w-full rounded-[20px] px-4 py-3.5 text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                                    :class="rowClass(group)"
                                    @click="toggleGroup(group.key)"
                                >
                                    <div class="flex items-start justify-between gap-3">
                                        <div class="min-w-0">
                                            <div class="flex items-center gap-2 flex-wrap">
                                                <div class="text-sm font-semibold text-base-content">{{ $tt(group.label) }}</div>
                                                <div class="badge badge-sm border-0 font-medium" :class="softBadgeClass(group.tone)">{{ groupBadge(group) }}</div>
                                            </div>
                                            <div class="mt-1.5 text-xs leading-5 text-base-content/55">
                                                {{ $tt(groupDescription(group)) }}
                                            </div>
                                        </div>
                                        <div class="shrink-0 text-right">
                                            <div class="text-lg font-semibold text-base-content">{{ group.count }}</div>
                                            <div class="text-[11px] text-base-content/40">{{ $tt('条') }}</div>
                                        </div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </aside>

                    <section class="min-w-0 scrape-overview-detail overflow-y-auto">
                        <div v-if="activeGroup" class="px-6 py-6">
                            <div class="flex flex-col gap-4 rounded-[24px] bg-base-100/65 px-5 py-5 lg:flex-row lg:items-start lg:justify-between">
                                <div class="min-w-0">
                                    <div class="flex items-center gap-3 flex-wrap">
                                        <h3 class="text-2xl font-semibold text-base-content">{{ $tt(activeGroup.label) }}</h3>
                                        <div class="badge badge-lg border-0 font-medium" :class="softBadgeClass(activeGroup.tone)">{{ activeGroup.count }}</div>
                                    </div>
                                    <p class="mt-2 max-w-3xl text-sm leading-6 text-base-content/65">
                                        {{ $tt(groupDescription(activeGroup)) }} {{ $tt('点击列表中的文件可查看详细元数据') }}
                                    </p>
                                </div>
                                <div class="flex flex-wrap gap-2">
                                    <button
                                        v-if="activeGroupKey === 'candidate' && detailData.candidate_count > 0"
                                        class="btn btn-sm btn-primary"
                                        @click="handleBatchAiCandidate"
                                    >{{ $tt('批量 AI 候选') }}</button>
                                    <button
                                        v-if="activeGroupKey === 'fail' && detailData.fail_count > 0"
                                        class="btn btn-sm btn-outline btn-error"
                                        @click="handleDeleteAllFail"
                                    >{{ $tt('批量删除失败文件') }}</button>
                                    <button
                                        v-if="activeGroup.key === 'miss_tag'"
                                        class="btn btn-sm btn-outline"
                                        @click="handleRedirectHome('unmatched')"
                                    >{{ $tt('跳转操作台') }}</button>
                                </div>
                            </div>

                            <div class="mt-5">
                                <div v-if="isGroupLoading(activeGroup.key)" class="flex items-center justify-center rounded-[24px] border border-dashed border-base-300 bg-base-100 py-14 text-base-content/60">
                                    <span class="loading loading-spinner loading-md mr-3"></span>
                                    {{ $tt('正在加载分类明细...') }}
                                </div>

                                <div v-else-if="activeItems.length === 0" class="rounded-[24px] border border-dashed border-base-300 bg-base-100 py-14 text-center text-base-content/55">
                                    {{ $tt('当前分类暂无数据') }}
                                </div>

                                <div v-else class="overflow-hidden rounded-[24px] border border-base-300/70 bg-base-100/95 shadow-sm">
                                    <div class="overflow-x-auto">
                                        <table class="table scrape-overview-table">
                                            <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>{{ $tt('文件名') }}</th>
                                                <th>{{ $tt('原因') }}</th>
                                                <th>{{ $tt('标题 / 艺术家 / 专辑') }}</th>
                                                <th>{{ $tt('操作') }}</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr v-for="(item, index) in activeItems" :key="item.id" class="border-t border-base-200/80">
                                                <th>{{ getRowNumber(activeGroup.key, index) }}</th>
                                                <td class="align-top">
                                                    <button class="link link-hover font-medium text-left" @click="openItemDetail(item)">{{ item.name }}</button>
                                                    <div class="mt-1 text-xs text-base-content/45 break-all scrape-path-clamp">{{ item.path }}</div>
                                                </td>
                                                <td class="align-top">
                                                    <div class="scrape-reason-clamp whitespace-pre-wrap break-words text-sm text-base-content/70">{{ item.reason || '-' }}</div>
                                                </td>
                                                <td class="align-top">
                                                    <div class="text-sm leading-6 min-w-[180px]">
                                                        <div>{{ item.title || '-' }}</div>
                                                        <div class="text-base-content/55">{{ item.artist || '-' }}</div>
                                                        <div class="text-base-content/45">{{ item.album || '-' }}</div>
                                                    </div>
                                                </td>
                                                <td class="align-top">
                                                    <div class="flex max-w-[220px] flex-wrap gap-2 text-sm xl:max-w-[260px]">
                                                        <button v-if="showCandidateAction(item)" class="scrape-action-btn" @click="handleBeixuan(item)">{{ $tt('候选') }}</button>
                                                        <button v-if="showCandidateAction(item)" class="scrape-action-btn scrape-action-btn--strong" @click="handleAiCandidate(item)">{{ $tt('AI候选') }}</button>
                                                        <button v-if="showEditAction(item)" class="scrape-action-btn scrape-action-btn--strong" @click="handleEditDetail(item)">{{ $tt('编辑') }}</button>
                                                        <button v-if="showFinishAction(item)" class="scrape-action-btn scrape-action-btn--success" @click="handleFinish(item)">{{ $tt('完成') }}</button>
                                                        <button v-if="showDeleteAction(item)" class="scrape-action-btn" @click="handleDelete(item)">{{ $tt('删除文件') }}</button>
                                                        <button v-if="showStopAction(item)" class="scrape-action-btn scrape-action-btn--danger" @click="handleStopDetail(item.id)">{{ $tt('停止') }}</button>
                                                    </div>
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <div v-if="shouldShowPager(activeGroup.key)" class="flex items-center justify-between border-t border-base-300/70 px-4 py-3">
                                        <div class="text-sm text-base-content/55">
                                            {{ $tt('共 {count} 条', { count: getGroupState(activeGroup.key).total }) }}
                                        </div>
                                        <div class="join">
                                            <button class="join-item btn btn-sm" :class="{'btn-disabled': isPrevDisabled(activeGroup.key)}" @click="changeActivePage(getGroupState(activeGroup.key).page - 1)">«</button>
                                            <button class="join-item btn btn-sm">{{ $tt('第 {page} / {pages} 页', { page: getGroupState(activeGroup.key).page, pages: getGroupState(activeGroup.key).totalPages || 1 }) }}</button>
                                            <button class="join-item btn btn-sm" :class="{'btn-disabled': isNextDisabled(activeGroup.key)}" @click="changeActivePage(getGroupState(activeGroup.key).page + 1)">»</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-else class="flex min-h-[420px] items-center justify-center px-6 py-12">
                            <div class="max-w-md text-center">
                                <div class="mx-auto flex h-14 w-14 items-center justify-center rounded-[20px] bg-base-200/80 text-base-content/45">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V9m-7-4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                </div>
                                <div class="mt-4 text-lg font-semibold text-base-content">{{ $tt('选择一个分类查看明细') }}</div>
                                <div class="mt-2 text-sm leading-6 text-base-content/55">
                                    {{ $tt('左侧保留全部分类结果，右侧只打开当前上下文，减少来回滚动和视觉干扰。') }}
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>

        <div class="drawer drawer-end">
            <input id="scrape-detail-drawer" type="checkbox" class="drawer-toggle" v-model="isItemDrawerOpen" />
            <div class="drawer-content"></div>
            <div class="drawer-side z-40">
                <label for="scrape-detail-drawer" aria-label="close sidebar" class="drawer-overlay"></label>
                <div class="scrape-detail-drawer min-h-full w-full max-w-2xl p-5 md:p-6 shadow-2xl">
                    <div class="flex items-start justify-between gap-4 border-b border-base-300/80 pb-4">
                        <div>
                            <div class="text-xs uppercase tracking-[0.24em] text-base-content/45">{{ $tt('Detail Payload') }}</div>
                            <h3 class="mt-2 text-xl font-semibold text-base-content">{{ selectedItemDetail.name || $tt('文件详情') }}</h3>
                            <div class="mt-2 text-sm text-base-content/55 break-all">{{ selectedItemDetail.path || '' }}</div>
                        </div>
                        <button class="btn btn-sm btn-ghost" @click="closeItemDrawer">{{ $tt('关闭') }}</button>
                    </div>

                    <div v-if="itemDetailLoading" class="flex items-center justify-center py-16 text-base-content/60">
                        <span class="loading loading-spinner loading-md mr-3"></span>
                        {{ $tt('正在加载详情...') }}
                    </div>

                    <div v-else class="space-y-5 pt-6">
                        <div class="grid gap-4 md:grid-cols-2">
                            <div class="rounded-[22px] bg-base-100/78 p-4">
                                <div class="text-xs uppercase tracking-[0.18em] text-base-content/45">{{ $tt('状态') }}</div>
                                <div class="mt-2 text-lg font-semibold">{{ $tt(selectedItemDetail.status_display || selectedItemDetail.status || '-') }}</div>
                            </div>
                            <div class="rounded-[22px] bg-base-100/78 p-4">
                                <div class="text-xs uppercase tracking-[0.18em] text-base-content/45">{{ $tt('原因') }}</div>
                                <div class="mt-2 text-sm leading-6 whitespace-pre-wrap break-words">{{ selectedItemDetail.reason || '-' }}</div>
                            </div>
                        </div>

                        <div class="rounded-[24px] border border-base-300/70 bg-base-100/82 p-5">
                            <div class="text-sm font-semibold mb-3">{{ $tt('元数据结果') }}</div>
                            <div class="grid gap-3 md:grid-cols-3 text-sm">
                                <div>
                                    <div class="text-base-content/45">{{ $tt('标题') }}</div>
                                    <div class="mt-1">{{ selectedItemDetail.title || '-' }}</div>
                                </div>
                                <div>
                                    <div class="text-base-content/45">{{ $tt('艺术家') }}</div>
                                    <div class="mt-1">{{ selectedItemDetail.artist || '-' }}</div>
                                </div>
                                <div>
                                    <div class="text-base-content/45">{{ $tt('专辑') }}</div>
                                    <div class="mt-1">{{ selectedItemDetail.album || '-' }}</div>
                                </div>
                            </div>
                        </div>

                        <div v-if="selectedItemDetail.extra && Object.keys(selectedItemDetail.extra).length" class="rounded-[24px] border border-base-300/70 bg-base-100/82 p-5">
                            <div class="text-sm font-semibold mb-3">{{ $tt('原始识别信息') }}</div>
                            <pre class="whitespace-pre-wrap break-words rounded-[20px] bg-base-200/88 p-4 text-xs leading-6">{{ formatJson(selectedItemDetail.extra) }}</pre>
                        </div>

                        <div v-if="selectedItemDetail.candidate_list && selectedItemDetail.candidate_list.length" class="rounded-[24px] border border-base-300/70 bg-base-100/82 p-5">
                            <div class="flex items-center justify-between gap-3">
                                <div class="text-sm font-semibold">{{ $tt('候选列表') }}</div>
                                <div class="badge badge-outline">{{ selectedItemDetail.candidate_list.length }}</div>
                            </div>
                            <div class="mt-4 grid gap-3">
                                <div v-for="(candidate, index) in selectedItemDetail.candidate_list" :key="index" class="rounded-[20px] bg-base-200/88 p-4 text-sm">
                                    <div class="flex items-start justify-between gap-3">
                                        <div class="min-w-0">
                                            <div class="font-medium break-words">{{ candidate.name || '-' }}</div>
                                            <div class="mt-1 break-words text-base-content/60">{{ candidate.artist || '-' }} / {{ candidate.album || '-' }}</div>
                                        </div>
                                        <div class="shrink-0 rounded-full bg-base-100/80 px-2.5 py-1 text-[11px] font-medium text-base-content/55">
                                            {{ candidate.resource || '-' }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'autoEditLogDetail',
    props: {
        detailData: { type: Object, required: true },
        groupPagination: { type: Object, default: () => ({}) },
        canResumeTask: { type: Boolean, default: false }
    },
    data() {
        return {
            activeGroupKey: '',
            isItemDrawerOpen: false,
            itemDetailLoading: false,
            selectedItemDetail: {}
        }
    },
    computed: {
        visibleGroups() {
            return (this.detailData.detail_groups || []).filter((group) => (group.count || 0) > 0)
        },
        activeGroup() {
            return this.visibleGroups.find((group) => group.key === this.activeGroupKey) || null
        },
        activeItems() {
            if (!this.activeGroupKey) {
                return []
            }
            return this.detailData[this.activeGroupKey] || []
        },
        headerStatusText() {
            return this.$tt(this.detailData.status_display || '-')
        },
        headerStatusClass() {
            const map = {
                running: 'bg-info/12 text-info',
                success: 'bg-success/12 text-success',
                fail: 'bg-error/12 text-error',
                stop: 'bg-warning/16 text-warning',
                miss: 'bg-warning/16 text-warning'
            }
            return map[this.detailData.status] || 'bg-base-200 text-base-content/70'
        },
        operationCategoryTitle() {
            return this.detailData.action || this.detailData.name || '操作详情'
        }
    },
    watch: {
        visibleGroups: {
            immediate: true,
            handler(groups) {
                if (!groups || groups.length === 0) {
                    this.activeGroupKey = ''
                    return
                }
                const exists = groups.some((group) => group.key === this.activeGroupKey)
                if (!exists) {
                    this.activeGroupKey = groups[0].key
                    this.fetchGroupPage(this.activeGroupKey, this.getGroupState(this.activeGroupKey).page || 1)
                }
            }
        }
    },
    methods: {
        groupBadge(group) {
            const map = {
                success: 'Exact',
                mode_success: 'Flexible',
                candidate: 'Review',
                fail: 'Error',
                running: 'Live',
                miss_tag: 'Unmatched',
                miss_artist_album: 'Missing',
                miss_artist: 'Missing',
                miss_album: 'Missing',
                skip: 'Skip'
            }
            return map[group.key] || 'Group'
        },
        groupDescription(group) {
            const map = {
                success: '标准匹配成功，结果通常可以直接入库。',
                mode_success: '在宽松模式下完成匹配，建议抽样确认准确性。',
                candidate: '存在多个可能结果，需要人工或 AI 辅助确认。',
                fail: '文件异常、网络异常或处理链路中断，需要优先排查。',
                running: '当前仍在处理中，数据会随任务轮询持续更新。',
                miss_tag: '无法从现有信息中完成匹配，通常需要回到操作台处理。',
                miss_artist_album: '缺少艺术家与专辑信息，匹配上下文不足。',
                miss_artist: '缺少艺术家信息，建议补齐后再尝试匹配。',
                miss_album: '缺少专辑信息，影响检索准确性。',
                skip: '已跳过处理，不会继续自动流转。'
            }
            return map[group?.key] || '当前分类包含需要进一步关注的任务项。'
        },
        rowClass(group) {
            const active = this.activeGroupKey === group.key
            const toneMap = {
                success: active ? 'bg-success/10 ring-1 ring-success/30' : 'hover:bg-base-200/70',
                info: active ? 'bg-info/10 ring-1 ring-info/30' : 'hover:bg-base-200/70',
                warning: active ? 'bg-warning/10 ring-1 ring-warning/30' : 'hover:bg-base-200/70',
                error: active ? 'bg-error/10 ring-1 ring-error/30' : 'hover:bg-base-200/70',
                accent: active ? 'bg-accent/10 ring-1 ring-accent/30' : 'hover:bg-base-200/70',
                neutral: active ? 'bg-base-200 ring-1 ring-base-300' : 'hover:bg-base-200/70',
                ghost: active ? 'bg-base-200 ring-1 ring-base-300' : 'hover:bg-base-200/70'
            }
            return toneMap[group.tone] || toneMap.neutral
        },
        badgeClass(tone) {
            const map = {
                success: 'badge-success',
                info: 'badge-info',
                warning: 'badge-warning',
                error: 'badge-error',
                accent: 'badge-accent',
                neutral: 'badge-neutral',
                ghost: 'badge-ghost'
            }
            return map[tone] || 'badge-neutral'
        },
        softBadgeClass(tone) {
            const map = {
                success: 'bg-success/12 text-success',
                info: 'bg-info/12 text-info',
                warning: 'bg-warning/16 text-warning',
                error: 'bg-error/12 text-error',
                accent: 'bg-accent/12 text-accent',
                neutral: 'bg-base-200 text-base-content/70',
                ghost: 'bg-base-200 text-base-content/60'
            }
            return map[tone] || map.neutral
        },
        toggleGroup(groupKey) {
            this.activeGroupKey = groupKey
            this.fetchGroupPage(groupKey, this.getGroupState(groupKey).page || 1)
        },
        closeGroup() {
            this.activeGroupKey = ''
        },
        fetchGroupPage(groupKey, page) {
            this.$emit('group-page-change', { group: groupKey, page })
        },
        changeActivePage(page) {
            if (!this.activeGroup) {
                return
            }
            const state = this.getGroupState(this.activeGroup.key)
            const normalizedPage = Math.max(1, Math.min(page, state.totalPages || 1))
            this.fetchGroupPage(this.activeGroup.key, normalizedPage)
        },
        getGroupState(group) {
            return this.groupPagination?.[group] || {
                page: 1,
                pageSize: 10,
                total: 0,
                totalPages: 0,
                loading: false
            }
        },
        getRowNumber(group, index) {
            const state = this.getGroupState(group)
            return (state.page - 1) * state.pageSize + index + 1
        },
        shouldShowPager(group) {
            return this.getGroupState(group).totalPages > 1
        },
        isPrevDisabled(group) {
            return this.getGroupState(group).page <= 1
        },
        isNextDisabled(group) {
            const state = this.getGroupState(group)
            return state.totalPages <= 0 || state.page >= state.totalPages
        },
        isGroupLoading(group) {
            return !!this.getGroupState(group).loading
        },
        async openItemDetail(item) {
            this.isItemDrawerOpen = true
            this.itemDetailLoading = true
            this.selectedItemDetail = item
            try {
                const res = await this.$api.Task.getAuditLogDetailItem({
                    id: this.detailData.id,
                    detail_id: item.id
                })
                if (res.result) {
                    this.selectedItemDetail = res.data
                }
            } finally {
                this.itemDetailLoading = false
            }
        },
        closeItemDrawer() {
            this.isItemDrawerOpen = false
        },
        formatJson(value) {
            try {
                return JSON.stringify(value || {}, null, 2)
            } catch (error) {
                return ''
            }
        },
        showCandidateAction(item) {
            return item.status === 'scrape_candidate'
        },
        showEditAction(item) {
            return ['scrape_candidate', 'scrape_no_match', 'scrape_miss_album', 'scrape_miss_artist_album', 'scrape_failed'].includes(item.status)
        },
        showFinishAction(item) {
            return ['scrape_candidate', 'scrape_no_match', 'scrape_miss_album', 'scrape_miss_artist_album', 'scrape_failed'].includes(item.status)
        },
        showDeleteAction(item) {
            return item.status === 'scrape_failed'
        },
        showStopAction(item) {
            return item.status === 'running'
        },
        handleStopDetail(id) {
            this.$emit('stop-detail', id)
        },
        handleBeixuan(item) {
            this.$emit('beixuan', item)
        },
        handleAiCandidate(item) {
            this.$emit('ai-candidate', item)
        },
        handleEditDetail(item) {
            this.$emit('edit-detail', item)
        },
        handleFinish(item) {
            this.$emit('finish', item)
        },
        handleDelete(item) {
            this.$emit('delete', item)
        },
        handleDeleteAllFail() {
            this.$emit('delete-all-fail')
        },
        handleBatchAiCandidate() {
            this.$emit('batch-ai-candidate')
        },
        getVirtualStatusFolder(status) {
            const normalizedStatus = String(status || '').trim().toLowerCase()
            const folderMap = {
                unmatched: '未匹配',
                candidate: '未匹配',
                '未匹配': '未匹配'
            }
            return folderMap[normalizedStatus] || '未匹配'
        },
        handleRedirectHome(status) {
            const auditLogId = this.detailData.id;
            const action = this.detailData.action || this.detailData.name || 'auto_scrape';
            const createdAt = this.detailData.created_at || '';
            const statusFolder = this.getVirtualStatusFolder(status);
            const virtualPath = `/app/virtual/audit/#${auditLogId} ${action}-${createdAt}/${statusFolder}`;
            this.$router.push({
                name: 'home',
                query: {
                    path: virtualPath
                }
            });
        }
    }
}
</script>

<style scoped>
.scrape-overview-shell {
    background: linear-gradient(
        180deg,
        oklch(var(--b2) / 0.42) 0%,
        oklch(var(--b1) / 0.94) 100%
    );
}

.scrape-overview-sidebar {
    background: linear-gradient(
        180deg,
        oklch(var(--b2) / 0.72) 0%,
        oklch(var(--b1) / 0.98) 100%
    );
}

.scrape-overview-detail {
    background: linear-gradient(
        180deg,
        oklch(var(--b1)) 0%,
        oklch(var(--b2) / 0.22) 100%
    );
}

.scrape-overview-table thead tr {
    background: oklch(var(--b2) / 0.58);
}

.scrape-overview-table tbody tr {
    transition: background-color 180ms ease;
}

.scrape-overview-table tbody tr:hover {
    background: oklch(var(--b2) / 0.36);
}

.scrape-overview-table th,
.scrape-overview-table td {
    vertical-align: top;
}

.scrape-overview-table th {
    white-space: nowrap;
}

.scrape-overview-table thead th {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: oklch(var(--bc) / 0.58);
}

.scrape-detail-drawer {
    background: linear-gradient(
        180deg,
        oklch(var(--b2)) 0%,
        oklch(var(--b1)) 100%
    );
}

.scrape-action-btn {
    border-radius: 9999px;
    padding: 0.2rem 0.65rem;
    color: oklch(var(--bc) / 0.62);
    background: oklch(var(--b2) / 0.55);
    transition: background-color 180ms ease, color 180ms ease;
}

.scrape-action-btn:hover {
    color: oklch(var(--bc) / 0.9);
    background: oklch(var(--b2) / 0.88);
}

.scrape-action-btn--strong {
    color: oklch(var(--p) / 0.8);
}

.scrape-action-btn--success {
    color: oklch(var(--su) / 0.82);
}

.scrape-action-btn--danger {
    color: oklch(var(--er) / 0.82);
}

.scrape-path-clamp {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.scrape-reason-clamp {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    max-width: 20rem;
}

@media (max-width: 1023px) {
    .scrape-overview-table {
        min-width: 980px;
    }
}

@media (max-width: 767px) {
    .scrape-detail-drawer {
        max-width: 100%;
    }

    .scrape-reason-clamp {
        max-width: 16rem;
    }
}
</style>
